import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { MaterialCombobox } from '@/components/form/inputs/MaterialCombobox';
import { useTranslation } from '@/hooks/useTranslation';
import { Layers, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SectionLabel } from '@/components/ui/section-label';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { EmptyState } from '@/components/shared/EmptyState';
import type { Material } from '../../type/Material';
import type { TraceabilityApiClient } from '../../api/TraceabilityApiClient';
import { flattenIsoTree } from '../../data/isoMaterialTree';

type MaterialTabProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  traceabilityApiClient: TraceabilityApiClient | null;
  companyName?: string;
};

export type MaterialTabHandle = {
  validate: () => boolean;
  getValues: () => { material: Material | null; supplier: string; lotNumber: string };
};

export const MaterialTab = forwardRef<MaterialTabHandle, MaterialTabProps>(
  function MaterialTab({ traceabilityApiClient, onChange }, ref) {
    const { t } = useTranslation();
    const isoOptions = useMemo(() => flattenIsoTree(t), [t]);

    const [materials, setMaterials] = useState<Material[]>([]);
    const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
    const [supplier, setSupplier] = useState('');
    const [lotNumber, setLotNumber] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!traceabilityApiClient) return;
      const ac = new AbortController();
      setIsLoadingMaterials(true);
      traceabilityApiClient
        .materialReadAll({}, { signal: ac.signal })
        .then((res) => {
          if (ac.signal.aborted) return;
          if (res.ok && res.data?.materials) {
            setMaterials(
              res.data.materials
                .map((item: { material: Material }) => item.material)
                .filter((m: Material) => !m.archived)
            );
          }
        })
        .catch((err: unknown) => {
          if (ac.signal.aborted) return;
          console.error('Failed to fetch materials for tab:', err);
        })
        .finally(() => {
          if (!ac.signal.aborted) setIsLoadingMaterials(false);
        });
      return () => ac.abort();
    }, [traceabilityApiClient]);

    useImperativeHandle(ref, () => ({
      validate: () => {
        if (selectedMaterial === null) {
          setError(t('traceability.materialTab.errors.required'));
          return false;
        }
        setError(null);
        return true;
      },
      getValues: () => ({ material: selectedMaterial, supplier, lotNumber }),
    }));

    const selectedMaterialId = selectedMaterial?.id ?? null;
    const isEmpty = !isLoadingMaterials && materials.length === 0;

    if (isEmpty) {
      return (
        <EmptyState
          icon={Layers}
          title={t('traceability.materialTab.empty.title')}
          description={t('traceability.materialTab.empty.hint')}
        />
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Left card — Material picker */}
        <Card padding="sm">
          <div className="flex flex-col gap-2">
            <div className="space-y-1">
              <SectionLabel icon={Layers}>
                {t('traceability.materialTab.materialTitle')}
              </SectionLabel>
              <p className="text-sm text-muted-foreground">
                {t('traceability.materialTab.materialDescription')}
              </p>
            </div>
            <div className="mt-3">
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                {t('traceability.materialTab.picker.label')}
              </p>
              <MaterialCombobox
                materials={materials}
                selectedId={selectedMaterialId}
                onSelect={(id) => {
                  const mat = id !== null ? (materials.find((m) => m.id === id) ?? null) : null;
                  setSelectedMaterial(mat);
                  onChange('material', mat);
                  if (id !== null) setError(null);
                }}
                placeholder={t('traceability.materialTab.picker.placeholder')}
                searchPlaceholder={t('traceability.materialTab.picker.searchPlaceholder')}
                noResults={t('traceability.materialTab.picker.noResults')}
                clearLabel={t('common.clearSelection')}
                error={error}
                isoOptions={isoOptions}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                {t('traceability.materialTab.picker.hint')}
              </p>
            </div>
          </div>
        </Card>

        {/* Right card — Lot details */}
        <Card padding="sm">
          <div className="flex flex-col gap-2">
            <div className="space-y-1">
              <SectionLabel icon={Truck}>
                {t('traceability.materialTab.lot.title')}
                <Badge size="sm" className="ml-1 normal-case tracking-normal">
                  {t('traceability.materialTab.lot.optional')}
                </Badge>
              </SectionLabel>
            </div>
            <div
              className={cn(
                'mt-3 grid gap-3 transition-opacity',
                selectedMaterialId === null && 'opacity-50 pointer-events-none'
              )}
            >
              <SimpleInput
                name="supplier"
                label={t('traceability.materialTab.lot.supplierLabel')}
                value={supplier}
                onChange={(val) => { setSupplier(val as string); onChange('materialLotSupplier', val); }}
                placeholder={t('traceability.materialTab.lot.supplierPlaceholder')}
                readOnly={selectedMaterialId === null}
              />
              <SimpleInput
                name="lotNumber"
                label={t('traceability.materialTab.lot.lotLabel')}
                value={lotNumber}
                onChange={(val) => { setLotNumber(val as string); onChange('materialLotNumber', val); }}
                placeholder={t('traceability.materialTab.lot.lotPlaceholder')}
                readOnly={selectedMaterialId === null}
              />
            </div>
            <Callout tone="neutral" className="mt-2">
              {t('traceability.materialTab.lot.hint')}
            </Callout>
          </div>
        </Card>
      </div>
    );
  }
);
